import { FC, memo } from 'react';
import {
  Box,
  Button,
  Heading,
  HStack,
  Icon,
  Image,
  Link,
  Stack,
  Text,
  VStack,
} from '@chakra-ui/react';
import dayjs from 'dayjs';
import { AppContextProps, useAppContext } from '@frx/frx-react-context';
import { useNavigate } from 'react-router-dom';
import isEmpty from 'lodash/fp/isEmpty';
import styles from './EventDetails.module.scss';
import { Card } from '../card';
import { getDataTracking } from '../../utils';
import { EditPencilSolid, EventDetailsImg, External } from '../../assets';
import { AddToCalendar } from '../add-to-calendar';

export interface EventDetailProps {
  image?: string;
  isEditable?: boolean;
  eventInfo?: AppContextProps['data']['eventData']['eventInfo'];
}

/**
 * @description If the participation type is a standard type then there is no need to display it.
 * @param participationType
 * @returns boolean
 */
const shouldDisplayParticipationType = (participationType: string): boolean =>
  !isEmpty(participationType) &&
  !participationType.includes('Participant') &&
  !participationType.includes('St. Jude Fundraiser');

export const EventDetails: FC<EventDetailProps> = memo(
  ({ isEditable = true, eventInfo, image }: EventDetailProps) => {
    const navigateTo = useNavigate();
    const {
      data: {
        authData: { user },
      },
    } = useAppContext();

    const handleEditReg = () => navigateTo('/registration');
    const participationType = user?.partType || '';

    const body = `<h3>${eventInfo?.name} (${eventInfo?.title})</h3></br>
    <p>Event details: </p>
    <ul>
       <li>Event Schedule: ${eventInfo?.schedule}</li>
       <li>Event Map: ${eventInfo?.mapLink}</li>
       <li>Event Info: ${eventInfo?.sponsor5}</li>
    </ul>
    <p>Any queries please contact: ${eventInfo?.coordinator?.name} 
      <a href="mailto:${eventInfo?.coordinator?.email}">
        ${eventInfo?.coordinator?.email}
      </a>
    </p>
    <p>Also, here is the event page ${eventInfo?.greeting_url}</p>`;

    return (
      <Card w={['full', 'full', '356px']}>
        <Heading size="md" color="brand.primary.swatch1">
          Event Details
        </Heading>
        <Stack mt={4} spacing={6}>
          <Box
            borderWidth="px"
            borderRadius="8px"
            overflow="hidden"
            boxShadow="0px 0px 6px rgba(0,0,0,0.20)"
          >
            <Box className={styles.eventDetailShareCard}>
              <Link
                href={eventInfo?.greeting_url}
                {...getDataTracking('eventDetailsImageLink')}
              >
                <Image
                  src={image}
                  alt={eventInfo?.name}
                  w="full"
                  fallbackSrc={EventDetailsImg}
                />
                <Box className={styles.eventDetailShare}>
                  <Icon boxSize={4} color="brand.secondary.swatch4">
                    <External />
                  </Icon>
                </Box>
              </Link>
            </Box>
            <Box p={5}>
              <HStack spacing={4}>
                {eventInfo?.event_date && (
                  <VStack spacing={0}>
                    <Text fontSize="1.6rem" lineHeight={1}>
                      {dayjs(eventInfo.event_date).format('DD')}
                    </Text>
                    <Text fontSize="sm">
                      {dayjs(eventInfo.event_date).format('MMM')}
                    </Text>
                  </VStack>
                )}
                <Stack spacing={2}>
                  <Text fontSize="md" align="left">
                    {eventInfo?.name}
                  </Text>
                  <Text
                    fontSize="sm"
                    align="left"
                    color="brand.monochromatic.500"
                    data-testid="cityStateContainer"
                  >
                    {eventInfo?.city ? `${eventInfo?.city},` : ''}
                    {eventInfo?.state}
                  </Text>
                </Stack>
              </HStack>
            </Box>
          </Box>

          {shouldDisplayParticipationType(participationType) && (
            <Box textAlign="center">
              <Text fontSize="sm" color="brand.monochromatic.800">
                Participating as a
              </Text>
              <Text fontSize="lg" fontWeight="bold">
                {participationType}
              </Text>
            </Box>
          )}

          <AddToCalendar
            eventData={{
              subject: eventInfo?.name,
              startDate: eventInfo?.event_date,
              details: body,
              location: eventInfo?.location,
            }}
          />
        </Stack>
        <Stack mt={3}>
          {isEditable && (
            <Button
              colorScheme="blue"
              variant="ghost"
              leftIcon={<EditPencilSolid />}
              onClick={handleEditReg}
              {...getDataTracking('eventDetailsEventRegButton')}
            >
              Edit Registration
            </Button>
          )}
        </Stack>
      </Card>
    );
  }
);
